import { useRef, useState } from "react";
import { auth } from "../lib/firebase";
import { authenticatedFetch } from "../lib/api";
import { refreshTokenIfNewlyVerified } from "../lib/emailVerification";
import { HomeMetrics } from "../types";
import type { LoadStateStatus } from "../components/LoadState";

/** The wire shape. `available` is read once, here, so it is not added to `HomeMetrics`. */
type HomeMetricsResponse = HomeMetrics & { available?: boolean; code?: string };

/**
 * Loads the per-user Home dashboard metrics from the backend (`GET /me/home-metrics`,
 * self-scoped to the signed-in user), plus a reset.
 *
 * ENG-3350: failures are no longer swallowed. They used to leave `homeMetrics` null, which Home read
 * as `hasData: false` — so an analytics outage greeted established users with "New here?".
 * `homeMetricsStatus` is "error" when the request fails OR when the server answers that it could not
 * read the figures (`available: false`): that payload is the empty one, and taking it at face value
 * is the same mistake one layer down. An older backend omits the field, and reads as available.
 */
export const useHomeMetrics = () => {
  const [homeMetrics, setHomeMetrics] = useState<HomeMetrics | null>(null);
  // "loading" until the first answer, not "loaded": nothing yet is not the same as no activity.
  const [homeMetricsStatus, setHomeMetricsStatus] = useState<LoadStateStatus>("loading");
  // ENG-3726: no figures are read for an unverified password signup. That is a new account, not a failed load.
  const [emailUnverified, setEmailUnverified] = useState(false);
  // The latest request wins. The auth listener can fire twice for one sign-in, and a slower failure
  // must not replace a success that already landed; reset() retires whatever is still in flight.
  const latestRequest = useRef(0);

  const fetchHomeMetrics = async (): Promise<HomeMetrics | null> => {
    if (!auth.currentUser) return null;
    const seq = ++latestRequest.current;
    setHomeMetricsStatus("loading");
    let data: HomeMetricsResponse | null = null;
    try {
      // The token keeps saying unverified after the link is opened, until it is refreshed.
      await refreshTokenIfNewlyVerified().catch(() => {});
      const response = await authenticatedFetch('/me/home-metrics');
      if (response.ok) data = (await response.json()) as HomeMetricsResponse;
    } catch {
      // A network failure is handled below, like any other.
    }
    if (seq !== latestRequest.current) return null;
    const unverified = data?.code === "email_unverified";
    setEmailUnverified(unverified);
    if (!data || (data.available === false && !unverified)) {
      setHomeMetricsStatus("error");
      return null;
    }
    setHomeMetrics(data);
    setHomeMetricsStatus("loaded");
    return data;
  };

  const reset = () => {
    latestRequest.current++;
    setHomeMetrics(null);
    setHomeMetricsStatus("loading");
    setEmailUnverified(false);
  };

  return { homeMetrics, homeMetricsStatus, emailUnverified, fetchHomeMetrics, setHomeMetrics, reset };
};
